// Custom Error Classes
export class AuthenticationError extends Error {
  constructor(message: string, public code?: string) {
    super(message)
    this.name = 'AuthenticationError'
  }
}

export class ValidationError extends Error {
  constructor(message: string, public field?: string, public code?: string) {
    super(message)
    this.name = 'ValidationError'
  }
}

export class NetworkError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message)
    this.name = 'NetworkError'
  }
}

export class PermissionError extends Error {
  constructor(message: string, public requiredRole?: string) {
    super(message)
    this.name = 'PermissionError'
  }
}

export class BusinessLogicError extends Error {
  constructor(message: string, public code?: string) {
    super(message)
    this.name = 'BusinessLogicError'
  }
}

// Error Response Types
export interface ErrorDetails {
  message: string
  code?: string
  field?: string
  timestamp: string
  context?: Record<string, any>
}

export interface ApiErrorResponse {
  success: false
  error: ErrorDetails
  requestId?: string
}

// Error Handler Utility
export class ErrorHandler {
  static handle(error: unknown, context?: Record<string, any>): ErrorDetails {
    const timestamp = new Date().toISOString()
    
    if (error instanceof AuthenticationError) {
      return {
        message: error.message,
        code: error.code || 'AUTH_ERROR',
        timestamp,
        context,
      }
    }
    
    if (error instanceof ValidationError) {
      return {
        message: error.message,
        code: error.code || 'VALIDATION_ERROR',
        field: error.field,
        timestamp,
        context,
      }
    }
    
    if (error instanceof NetworkError) {
      return {
        message: error.message,
        code: `NETWORK_ERROR_${error.statusCode || 'UNKNOWN'}`,
        timestamp,
        context,
      }
    }
    
    if (error instanceof PermissionError) {
      return {
        message: error.message,
        code: 'PERMISSION_DENIED',
        timestamp,
        context: { ...context, requiredRole: error.requiredRole },
      }
    }
    
    if (error instanceof BusinessLogicError) {
      return {
        message: error.message,
        code: error.code || 'BUSINESS_LOGIC_ERROR',
        timestamp,
        context,
      }
    }
    
    // Handle standard Error objects
    if (error instanceof Error) {
      return {
        message: error.message,
        code: 'UNKNOWN_ERROR',
        timestamp,
        context,
      }
    }
    
    // Handle unknown error types
    return {
      message: typeof error === 'string' ? error : 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
      timestamp,
      context,
    }
  }
  
  static getUserFriendlyMessage(error: unknown): string {
    if (error instanceof AuthenticationError) {
      return 'Authentication failed. Please check your credentials and try again.'
    }
    
    if (error instanceof ValidationError) {
      return error.message
    }
    
    if (error instanceof NetworkError) {
      if (error.statusCode === 404) {
        return 'The requested resource was not found.'
      }
      if (error.statusCode === 500) {
        return 'Server error. Please try again later.'
      }
      return 'Network error. Please check your connection and try again.'
    }
    
    if (error instanceof PermissionError) {
      return 'You do not have permission to perform this action.'
    }
    
    if (error instanceof BusinessLogicError) {
      return error.message
    }
    
    return 'An unexpected error occurred. Please try again.'
  }
}

// Result Pattern for Better Error Handling
export type Result<T, E = Error> = 
  | { success: true; data: T }
  | { success: false; error: E }

export const Result = {
  ok: <T>(data: T): Result<T, never> => ({ success: true, data }),
  err: <E = Error>(error: E): Result<never, E> => ({ success: false, error }),
}

// Async Result Helper
export async function asyncResult<T>(
  promise: Promise<T>
): Promise<Result<T, Error>> {
  try {
    const data = await promise
    return Result.ok(data)
  } catch (error) {
    return Result.err(error instanceof Error ? error : new Error(String(error)))
  }
}

// Validation Helper
export function validateField(value: any, field: string, rules: any): void {
  if (!value && rules.required) {
    throw new ValidationError(`${field} is required`, field, 'REQUIRED')
  }
  
  if (value && rules.pattern && !rules.pattern.test(value)) {
    throw new ValidationError(rules.message || `Invalid ${field}`, field, 'INVALID_FORMAT')
  }
  
  if (value && rules.minLength && value.length < rules.minLength) {
    throw new ValidationError(
      `${field} must be at least ${rules.minLength} characters`,
      field,
      'TOO_SHORT'
    )
  }
  
  if (value && rules.maxLength && value.length > rules.maxLength) {
    throw new ValidationError(
      `${field} must be no more than ${rules.maxLength} characters`,
      field,
      'TOO_LONG'
    )
  }
} 